import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { User } from '@supabase/supabase-js';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
  signInWithGitHub: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  signOut: async () => {},
  signInWithGitHub: async () => {}
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const userRef = useRef(user);
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    let isMounted = true;

    // Get initial session with timeout
    const getInitialSession = async () => {
      const timeoutPromise = new Promise<void>((resolve) =>
        setTimeout(resolve, 2000) // 2 second max loading time
      );

      try {
        const sessionPromise = supabase.auth.getSession();
        const [{ data: { session } }] = await Promise.all([sessionPromise, timeoutPromise]);

        if (isMounted) {
          if (session?.user) {
            userRef.current = session.user;
            setUser(session.user);
            console.log('Session user:', session.user);
          }
          setLoading(false);
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error getting session:', error);
          setLoading(false);
        }
      }
    };

    getInitialSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (!isMounted) return;

      const newUser = session?.user ?? null;

      // Clear any pending timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // If user is being removed, add a small delay
      if (!newUser && userRef.current) {
        timeoutRef.current = setTimeout(() => {
          if (isMounted) {
            userRef.current = newUser;
            setUser(newUser);
            setLoading(false);
          }
        }, 500); // 500ms delay before removing user
      } else {
        userRef.current = newUser;
        setUser(newUser);
        setLoading(false);
        if (newUser) {
          console.log('Auth state changed - new user:', newUser);
        }
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const signInWithGitHub = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'github',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
        scopes: 'read:user', // Minimal scope
      },
    });

    if (error) {
      console.error('Error signing in with GitHub:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, signOut, signInWithGitHub }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
